import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Grid,
    TextField, Switch, Button, Alert, CircularProgress, MenuItem,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../../constants";
import { loyaltyFetch } from "../../../hooks/loyaltyFetch";
import { useBrand } from "../../../../../contexts/BrandContext";

const INITIAL = { name: "", description: "", validity_days: "", active: true };

// Calendar-aware: shift today by N months/years and count real calendar days
const calendarDaysFromToday = (amount, unit) => {
    const n = Math.max(1, Math.floor(Number(amount)));
    if (unit === "days") return n;

    const today = new Date();
    const future = new Date(today);
    if (unit === "years") future.setFullYear(future.getFullYear() + n);
    else if (unit === "months") future.setMonth(future.getMonth() + n);
    return Math.round((future - today) / (1000 * 60 * 60 * 24));
};

const CreateCouponTypeDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";
    const [form, setForm] = useState({ ...INITIAL });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [unitQty, setUnitQty] = useState("");
    const [unitType, setUnitType] = useState("days");

    const handleChange = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const isValid = brandName && form.name.trim();

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            const body = {
                brand: brandName,
                name: form.name.trim(),
                description: form.description.trim() || null,
                validity_days: form.validity_days !== "" ? parseInt(form.validity_days, 10) : null,
                active: form.active,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/coupon-types`, {
                method: "POST",
                headers: { "Content-Type": "application/json", "X-Brand": brandName },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => { setForm({ ...INITIAL }); setError(null); setUnitQty(""); onClose(); };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.rewards.gradient }}>
                        <ConfirmationNumberIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouveau Type de Coupon
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom du type de coupon" value={form.name}
                            onChange={handleChange("name")} size="small" disabled={!brandName}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handleChange("description")} size="small" multiline rows={2}
                            disabled={!brandName}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif" mb={0.75} color="#1a1a2e">
                            Validité
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" type="number" label="Quantité"
                                    value={unitQty} disabled={!brandName}
                                    placeholder="Illimité (laisser vide)"
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, "");
                                        setUnitQty(val);
                                        if (val) {
                                            setForm(p => ({ ...p, validity_days: String(calendarDaysFromToday(val, unitType)) }));
                                        } else {
                                            setForm(p => ({ ...p, validity_days: "" }));
                                        }
                                    }}
                                    InputProps={{ inputProps: { min: 0 } }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth select size="small" label="Unité"
                                    value={unitType} disabled={!brandName}
                                    onChange={(e) => {
                                        const newUnit = e.target.value;
                                        setUnitType(newUnit);
                                        if (unitQty) {
                                            setForm(p => ({ ...p, validity_days: String(calendarDaysFromToday(unitQty, newUnit)) }));
                                        }
                                    }}
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}>
                                    <MenuItem value="days">Jours</MenuItem>
                                    <MenuItem value="months">Mois</MenuItem>
                                    <MenuItem value="years">Années</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">
                                Activer ce type de coupon
                            </Typography>
                            <Switch checked={form.active}
                                onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                disabled={!brandName}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.rewards.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.rewards.main },
                                }} />
                        </Box>
                    </Grid>
                </Grid>
                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{ background: SECTION_COLORS.rewards.gradient, color: "#fff", textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3 }}>
                    {submitting ? "Création..." : "Créer le Type de Coupon"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateCouponTypeDialog;
